import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { VisibilityEnum } from "../model";
import i18n from "../../../i18n";

const translate = (key: string, options?: Record<string, any>) => i18n.t(key, options) as string;

interface GlobalServiceFunctions {
    getBaseUrl: () => string;
    getStorage: (key: string) => Promise<any>;
    setStorage: (key: string, value: any) => Promise<void>;
    removeStorage: (key: string) => Promise<void>;
    clearStorage: () => Promise<void>;
    showToast: (type: string, title: string, description: string) => any;
    hideToast: () => any;
    showAlertWithInput: (header: string) => any;
    showAlert: (header: string, description: string) => any
    showConfirmAlert: (header: string, description: string, cancelBtnText: string, confirmBtnText: string) => Promise<boolean>
}

const getBaseUrl = (): string => {
    return process.env.REACT_APP_API_BASE_URL;
};

const getStorage = async (key: string): Promise<any> => {
    try {
        const response = localStorage.getItem(key);
        return response ? JSON.parse(response) : null;
    } catch (error) {
        throw new Error(translate("globalService.errors.storageRetrieve"));
    }
};

const setStorage = async (key: string, value: any): Promise<void> => {
    try {
        await localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
        throw new Error(translate("globalService.errors.storageSet"));
    }
};

const removeStorage = async (key: string): Promise<void> => {
    try {
        await localStorage.removeItem(key);
    } catch (error) {
        throw new Error(translate("globalService.errors.storageRemove"));
    }
};

const clearStorage = async (): Promise<void> => {
    try {
        await localStorage.clear();
    } catch (error) {
        throw new Error(translate("globalService.errors.storageClear"));
    }
};

const showToast = (type: string, title: string, description: string): any => {
    const message = description ? `${title} - ${description}` : title;

    switch (type) {
        case "success":
            toast.success(message);
            break;
        case "error":
            toast.error(message);
            break;
        case "info":
            toast.info(message);
            break;
        case "warn":
        case "warning":
            toast.warn(message);
            break;
        default:
            toast(message);
            break;
    }
};

const hideToast = (): any => {
    toast.dismiss();
};

const showAlert = (header: string, description: string): any => {
    alert(`${header}\n${description}`);
};

const showAlertWithInput = (header: string): any => {
    return new Promise((resolve, reject) => {
        const result = prompt(header);
        if (result !== null) {
            resolve(result);
        } else {
            reject(false);
        }
    });
};

const showConfirmAlert = (
    header: string,
    description: string,
    cancelBtnText = translate("common.cancel"),
    confirmBtnText = translate("common.ok"),
): Promise<boolean> => {
    return new Promise((resolve, reject) => {
        const confirmed = window.confirm(`${header}\n${description}`);
        if (confirmed) {
            resolve(true);
        } else {
            reject(false);
        }
    });
};

const GlobalService: GlobalServiceFunctions = {
    getBaseUrl,
    getStorage,
    setStorage,
    removeStorage,
    clearStorage,
    showToast,
    hideToast,
    showAlertWithInput,
    showAlert,
    showConfirmAlert
};

 const industries = [
  { value: "1", label: translate("globalService.industries.technology") },
  { value: "2", label: translate("globalService.industries.finance") },
  { value: "3", label: translate("globalService.industries.healthcare") },
  { value: "4", label: translate("globalService.industries.education") },
  { value: "5", label: translate("globalService.industries.retail") },
  { value: "6", label: translate("globalService.industries.manufacturing") },
  { value: "7", label: translate("globalService.industries.professionalServices") },
];
 const companySizes = [
  { value: "1", label: translate("globalService.companySizes.oneToTen") },
  { value: "2", label: translate("globalService.companySizes.elevenToFifty") },
  { value: "3", label: translate("globalService.companySizes.fiftyOneToTwoHundred") },
  { value: "4", label: translate("globalService.companySizes.twoHundredOneToFiveHundred") },
  { value: "5", label: translate("globalService.companySizes.fiveHundredOneToThousand") },
  { value: "6", label: translate("globalService.companySizes.thousandPlus") },
];
const getLabelById = (arr: { value: string; label: string }[], id: string) => {
  const item = arr.find(i => i.value === id);
  return item ? item.label : "";
};
 const languagePreferences = [
  { value: "1", label: translate("globalService.languagePreferences.basic") },
  { value: "2", label: translate("globalService.languagePreferences.professional") },
  { value: "3", label: translate("globalService.languagePreferences.native") },
];
 const legalStatusOptions = [
  { value: "1", label: translate("globalService.legalStatus.limitedCompany") },
  { value: "2", label: translate("globalService.legalStatus.limitedLiabilityPartnership") },
  { value: "3", label: translate("globalService.legalStatus.partnership") },
  { value: "4", label: translate("globalService.legalStatus.soleTrader") },
  { value: "5", label: translate("globalService.legalStatus.publicLimitedCompany") },
];
const countryOptions = [
  { value: "", label: translate("globalService.countryOptions.select") },
  { value: "UK", label: translate("globalService.countryOptions.unitedKingdom") },
  { value: "US", label: translate("globalService.countryOptions.unitedStates") },
  { value: "CA", label: translate("globalService.countryOptions.canada") },
  { value: "AU", label: translate("globalService.countryOptions.australia") },
  // Add more countries as needed
];
const currencyOptions = [
    { label: translate("globalService.currencyOptions.select"), value: "" },
    { label: translate("globalService.currencyOptions.usd"), value: "USD" },
    { label: translate("globalService.currencyOptions.eur"), value: "EUR" },
    { label: translate("globalService.currencyOptions.gbp"), value: "GBP" },
];
const visibilityOptions = [
    { label: translate("globalService.visibility.public"), value: VisibilityEnum.Public },
    { label: translate("globalService.visibility.private"), value: VisibilityEnum.Private },
    { label: translate("globalService.visibility.contactsOnly"), value: VisibilityEnum.Contacts_Only },
];

const timezoneOptions = [
    { label: translate("globalService.timezoneOptions.select"), value: "" },
    { label: translate("globalService.timezoneOptions.gmt0"), value: "GMT+0" },
    { label: translate("globalService.timezoneOptions.gmtPlus1"), value: "GMT+1" },
    { label: translate("globalService.timezoneOptions.gmtMinus5"), value: "GMT-5" },
];
const getCurrentDateAndTime = (inputDate?): string => {
        const date = inputDate ? new Date(inputDate) : new Date();
        const hours = date.getHours() > 9 ? date.getHours() : "0" + date.getHours();
        const minutes = date.getMinutes() > 9 ? date.getMinutes() : "0" + date.getMinutes();
        const seconds = date.getSeconds() > 9 ? date.getSeconds() : "0" + date.getSeconds();
        const day = date.getDate() > 9 ? date.getDate() : "0" + date.getDate();
        const month = date.getMonth() + 1 > 9 ? date.getMonth() + 1 : "0" + (date.getMonth() + 1);
        const year = date.getFullYear() > 9 ? date.getFullYear() : "0" + date.getFullYear();
        return year + "-" + month + "-" + day + "T" + hours + ":" + minutes + ":" + seconds;
    };
function formatTimeTo12Hour(timeStr: string): string {
    if (!timeStr) return "";
    const [hourStr, minute] = timeStr.split(":");
    let hour = parseInt(hourStr, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12; // Convert to 12-hour format
    return `${hour}:${minute} ${ampm}`;
}

// Utility function to parse API errors and format them for toast messages
export const parseApiErrors = (error: any): string => {
    try {
        // Check if error has the expected structure
        if (error?.data?.errors && typeof error.data.errors === "object") {
            const errors = error.data.errors;
            const errorMessages: string[] = [];

            // Iterate through each field error
            Object.keys(errors).forEach(field => {
                const fieldError = errors[field];
                
                // Handle nested object errors (like { id: "error message" })
                if (typeof fieldError === "object" && fieldError !== null) {
                    Object.keys(fieldError).forEach(subField => {
                        const message = fieldError[subField];
                        if (typeof message === "string" && message.trim()) {
                            // Format the field name for better readability
                            const formattedField = field.replace(/([A-Z])/g, " $1").replace(/^./, str => str.toUpperCase());
                            errorMessages.push(`${formattedField}: ${message}`);
                        }
                    });
                } 
                // Handle direct string errors
                else if (typeof fieldError === "string" && fieldError.trim()) {
                    const formattedField = field.replace(/([A-Z])/g, " $1").replace(/^./, str => str.toUpperCase());
                    errorMessages.push(`${formattedField}: ${fieldError}`);
                }
            });

            // Return formatted error messages
            if (errorMessages.length > 0) {
                return errorMessages.join("\n");
            }
        }

        // Fallback error messages
        if (error?.data?.message) {
            return error.data.message;
        }

        if (error?.message) {
            return error.message;
        }

        // Default error message
        return translate("globalService.errors.generic");
    } catch (parseError) {
        console.error("Error parsing API errors:", parseError);
        return translate("globalService.errors.generic");
    }
};

export { GlobalService,industries,companySizes,legalStatusOptions,countryOptions,timezoneOptions,currencyOptions,getCurrentDateAndTime,visibilityOptions,getLabelById,formatTimeTo12Hour,languagePreferences };